import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { KycOnboardingStore } from '../store/kyc-onboarding.store';
import { TimezoneOnboardingStore } from '../store/timezone-onboarding.store';
import { DateFormatValue } from 'src/app/core/models/date-format.model';
import { VendorDateTimeService } from 'src/app/core/service/vendor-datetime.service';
import {
  getDefaultDateFormatForTimezone,
  isValidDateFormatValue,
} from 'src/app/core/utils/date-format.util';
import { getPreferredTimezoneId } from 'src/app/core/utils/timezone.util';
import { VendorService } from 'src/app/pages/vendor/vendor.service';
import { TimezoneSelectorComponent } from '../timezone-selector/timezone-selector.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-vendor-header-timezone',
  standalone: true,
  imports: [TimezoneSelectorComponent],
  templateUrl: './vendor-header-timezone.component.html',
  styleUrl: './vendor-header-timezone.component.css',
})
export class VendorHeaderTimezoneComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly vendorService = inject(VendorService);
  private readonly vendorDateTimeService = inject(VendorDateTimeService);
  private readonly toastrService = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly timezoneOnboardingStore = inject(TimezoneOnboardingStore);
  private readonly kycStore = inject(KycOnboardingStore);
  private readonly destroy$ = new Subject<void>();

  readonly selectedTimezone = signal<string | null>(null);
  readonly selectedDateFormat = signal<DateFormatValue | null>(null);
  readonly isSavingTimezone = signal(false);
  readonly timezoneSaveVersion = signal(0);

  ngOnInit(): void {
    this.syncSelectedTimezone(this.authService.getVendorData());

    this.authService.vendorDataChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncSelectedTimezone(this.authService.getVendorData()));

    this.vendorDateTimeService.preferencesChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncSelectedTimezone(this.authService.getVendorData()));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTimezoneSettingsSave(payload: { time_zone: string; date_format: DateFormatValue }): void {
    if (this.isSavingTimezone()) {
      return;
    }

    const preferredTimezone = getPreferredTimezoneId(payload.time_zone);
    const savePayload = { time_zone: preferredTimezone, date_format: payload.date_format };

    this.isSavingTimezone.set(true);

    this.vendorService.setTimezone(savePayload).subscribe({
      next: (response: any) => {
        this.isSavingTimezone.set(false);

        if (response?.status) {
          const existingVendorData = this.authService.getVendorData() ?? {};
          const updatedVendorData = {
            ...existingVendorData,
            time_zone: preferredTimezone,
            timezone: preferredTimezone,
            date_format: payload.date_format,
          };
          this.selectedTimezone.set(preferredTimezone);
          this.selectedDateFormat.set(payload.date_format);
          this.authService.setVendorData(updatedVendorData);
          this.vendorDateTimeService.notifyPreferencesChanged();
          this.timezoneSaveVersion.update((version) => version + 1);
          this.timezoneOnboardingStore.close();
          this.toastrService.success(
            response.message || 'Timezone and date format updated successfully.',
            'Success',
          );
          this.handlePostTimezoneSaveNavigation();
        } else {
          this.toastrService.error(
            response?.message || 'Failed to update timezone settings.',
            'Error',
          );
        }
      },
      error: (error: any) => {
        this.isSavingTimezone.set(false);
        this.toastrService.error(
          error?.error?.message || error?.error?.error || 'Failed to update timezone settings.',
          'Error',
        );
      },
    });
  }

  private syncSelectedTimezone(data?: any): void {
    const vendorData = data ?? this.authService.getVendorData();
    const timezone =
      vendorData?.timezone ??
      vendorData?.time_zone ??
      null;

    const dateFormatValue =
      vendorData?.date_format ??
      vendorData?.dateFormat ??
      null;

    const configuredTimezone = timezone?.trim()
      ? getPreferredTimezoneId(timezone.trim())
      : null;

    this.selectedTimezone.set(configuredTimezone);
    this.selectedDateFormat.set(
      this.resolveSelectedDateFormat(dateFormatValue, configuredTimezone),
    );

    if (configuredTimezone) {
      this.timezoneOnboardingStore.close();
    }
  }

  private resolveSelectedDateFormat(
    dateFormatValue: string | null | undefined,
    configuredTimezone: string | null,
  ): DateFormatValue | null {
    if (isValidDateFormatValue(dateFormatValue)) {
      return dateFormatValue;
    }

    if (configuredTimezone) {
      return getDefaultDateFormatForTimezone(configuredTimezone);
    }

    return null;
  }

  private handlePostTimezoneSaveNavigation(): void {
    if (!this.authService.getProfileComplete()) {
      this.kycStore.open();
      void this.router.navigate(['/complete-profile']);
      return;
    }

    void this.router.navigate(['/dashboard']);
  }
}
